import { Alert, Button, CircularProgress } from "@mui/material";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  CmpEventPaymentStatusEnum,
  CmpEventRegistrationStatusEnum,
  CmpEventSponsorshipRequestStatusEnum,
  CmpInvoiceStatusEnum,
  CmpStaffCheckoutModeEnum,
  useSaveCmpInvoiceCardCheckoutNoteMutation,
  useStartCmpEventRegistrationCardCheckoutMutation,
  useStartCmpEventSponsorshipCardCheckoutMutation,
} from "../../../../graphql/types";
import SecureCardCheckoutHandoff, {
  SecureCardCheckoutHandoffData,
  SecureCardCheckoutMode,
} from "../../Invoicing/SecureCardCheckoutHandoff";
import { useCardCheckoutNoteBeforeConfirm } from "../../Invoicing/useCardCheckoutNoteBeforeConfirm";
import {
  canStartEventSecureCardCheckout,
  getEventSecureCardUnavailableMessage,
  EVENT_SECURE_CARD_CHECKOUT_CONFIG,
  EventSecureCardCheckoutPurpose,
  EventSecureCardCheckoutReturnLike,
  EventSecureCardCheckoutSession,
  EventSecureCardCheckoutReadback,
  getEventSecureCardCheckoutStatus,
  normalizeEventSecureCardCheckout,
} from "./eventSecureCardCheckout";
import {
  completeEventSalesOperation,
  prepareEventSalesOperation,
  runEventSalesSingleFlight,
} from "./eventSalesOperation";
import { eventSalesCallerHandledApolloContext } from "./eventSalesApolloErrorPolicy";
import { buildStaffErrorFallback, getUserFacingErrorText, UserFacingError } from "../../../../utils/userFacingError";

export type EventSecureCardCheckoutPanelProps = {
  amountDueInCents: number;
  /** Shown above Confirm & Record Payment (Internal Notes, design). */
  beforeSubmit?: React.ReactNode;
  /** Process Payment Internal Notes, saved on the card payment. */
  cardNote: string;
  chamberId: string;
  eventId: string;
  invoiceId: string;
  invoiceStatus: CmpInvoiceStatusEnum;
  /** True while a card submit is open; the popup locks close and method. */
  onPaymentInProgressChange?: (inProgress: boolean) => void;
  onPaymentProcessed?: () => Promise<void> | void;
  onRefreshAuthoritativeStatus: () => Promise<EventSecureCardCheckoutReadback>;
  paymentStatus: CmpEventPaymentStatusEnum;
  purpose: EventSecureCardCheckoutPurpose;
  returnUrl?: string;
  sourceId: string;
  sourceStatus:
    CmpEventRegistrationStatusEnum | CmpEventSponsorshipRequestStatusEnum;
};

const toCheckoutModeEnum = (mode: SecureCardCheckoutMode) =>
  mode === "CUSTOM"
    ? CmpStaffCheckoutModeEnum.Custom
    : CmpStaffCheckoutModeEnum.HostedFallback;

const getDefaultReturnUrl = () =>
  `${window.location.origin}/secure-checkout/return`;

const EventSecureCardCheckoutPanel = ({
  amountDueInCents,
  beforeSubmit,
  cardNote,
  chamberId,
  eventId,
  invoiceId,
  invoiceStatus,
  onPaymentInProgressChange,
  onPaymentProcessed,
  onRefreshAuthoritativeStatus,
  paymentStatus,
  purpose,
  returnUrl = getDefaultReturnUrl(),
  sourceId,
  sourceStatus,
}: EventSecureCardCheckoutPanelProps): React.ReactElement => {
  const [startRegistrationCheckout] =
    useStartCmpEventRegistrationCardCheckoutMutation({
      context: eventSalesCallerHandledApolloContext,
    });
  const [startSponsorshipCheckout] =
    useStartCmpEventSponsorshipCardCheckoutMutation({
      context: eventSalesCallerHandledApolloContext,
    });
  const [saveCardCheckoutNote] = useSaveCmpInvoiceCardCheckoutNoteMutation({
    context: eventSalesCallerHandledApolloContext,
  });
  const saveCardNoteBeforeConfirm = useCardCheckoutNoteBeforeConfirm({
    note: cardNote,
    saveNote: async ({ checkoutSessionId, note }) => {
      const result = await saveCardCheckoutNote({
        variables: { input: { chamberId, checkoutSessionId, invoiceId, note } },
      });
      if (!result.data?.saveCmpInvoiceCardCheckoutNote) {
        throw new UserFacingError(
          "Internal Notes could not be saved. No payment was submitted. Submit the card again.",
        );
      }
    },
  });
  const [session, setSession] = useState<EventSecureCardCheckoutSession | null>(
    null,
  );
  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState<string | null>(null);
  const [startRetryNonce, setStartRetryNonce] = useState(0);
  const startInFlightRef = useRef(false);
  const failedStartSourceKeyRef = useRef("");
  const mountedRef = useRef(true);
  const paymentNotificationRef = useRef(false);
  const sourceKey = `${purpose}:${chamberId}:${eventId}:${sourceId}:${invoiceId}:${amountDueInCents}`;

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    // Clear the prior client secret before a changed amount or source can start
    // its replacement custom Session.
    setSession(null);
    setStartError(null);
    setStartRetryNonce(0);
    failedStartSourceKeyRef.current = "";
  }, [sourceKey]);

  const canStart = canStartEventSecureCardCheckout({
    amountDueInCents,
    invoiceStatus,
    paymentStatus,
    purpose,
    sourceStatus,
  });

  const status = useMemo(
    () =>
      session
        ? getEventSecureCardCheckoutStatus({
            expiresAtUtcMilli: session.expiresAtUtcMilli,
            invoiceStatus,
            nowUtcMilli: Date.now(),
            paymentStatus,
          })
        : "PENDING",
    [invoiceStatus, paymentStatus, session],
  );

  const notifyPaymentProcessed = useCallback(async () => {
    if (paymentNotificationRef.current || !onPaymentProcessed) return;
    paymentNotificationRef.current = true;
    try {
      await onPaymentProcessed();
    } catch (error) {
      paymentNotificationRef.current = false;
      throw error;
    }
  }, [onPaymentProcessed]);

  const refreshAuthoritativeStatus = useCallback(async () => {
    const readback = await onRefreshAuthoritativeStatus();
    // The parent can remove this panel when Paid changes the amount to zero.
    // Complete from this readback, even if that render has already unmounted it.
    if (readback.invoiceStatus === CmpInvoiceStatusEnum.Paid &&
      readback.paymentStatus === CmpEventPaymentStatusEnum.Paid) {
      await notifyPaymentProcessed();
    }
  }, [notifyPaymentProcessed, onRefreshAuthoritativeStatus]);

  const refreshAfterRejectedStart = useCallback(async () => {
    try {
      await refreshAuthoritativeStatus();
    } catch {
      // Keep the backend start error as the primary action message.
    }
  }, [refreshAuthoritativeStatus]);

  const executeStart = useCallback(
    async (
      mode: SecureCardCheckoutMode,
      fallbackForCheckoutSessionId?: string,
    ): Promise<EventSecureCardCheckoutSession | null> => {
      if ((!canStart && !session) || amountDueInCents <= 0) {
        return null;
      }

      setStarting(true);
      setStartError(null);
      const config = EVENT_SECURE_CARD_CHECKOUT_CONFIG[purpose];

      try {
        let checkoutResult: EventSecureCardCheckoutReturnLike | undefined;
        let completePreparedOperation: () => void;
        const sharedInput = {
          amountInCents: amountDueInCents,
          chamberId,
          eventId,
          ...(fallbackForCheckoutSessionId
            ? { fallbackForCheckoutSessionId }
            : {}),
          invoiceId,
          mode: toCheckoutModeEnum(mode),
        };

        if (purpose === "REGISTRATION") {
          const preparedOperation = await prepareEventSalesOperation({
            action: config.operationAction,
            chamberId,
            entityId: sourceId,
            eventId,
            input: {
              ...sharedInput,
              registrationId: sourceId,
            },
          });
          checkoutResult = (
            await startRegistrationCheckout({
              variables: { input: preparedOperation.input },
            })
          ).data?.startCmpEventRegistrationCardCheckout;
          completePreparedOperation = () => {
            completeEventSalesOperation({ operation: preparedOperation });
          };
        } else {
          const preparedOperation = await prepareEventSalesOperation({
            action: config.operationAction,
            chamberId,
            entityId: sourceId,
            eventId,
            input: {
              ...sharedInput,
              sponsorshipRequestId: sourceId,
            },
          });
          checkoutResult = (
            await startSponsorshipCheckout({
              variables: { input: preparedOperation.input },
            })
          ).data?.startCmpEventSponsorshipCardCheckout;
          completePreparedOperation = () => {
            completeEventSalesOperation({ operation: preparedOperation });
          };
        }

        if (!checkoutResult) {
          throw new Error(
            "The secure event payment form could not be created. Refresh the invoice and try again.",
          );
        }

        const authoritativeSession = normalizeEventSecureCardCheckout({
          expectedAmountInCents: amountDueInCents,
          expectedInvoiceId: invoiceId,
          requestedMode: mode,
          result: checkoutResult,
        });
        completePreparedOperation();

        if (mountedRef.current) {
          setSession(authoritativeSession);
        }
        return authoritativeSession;
      } catch (error) {
        await refreshAfterRejectedStart();
        if (mountedRef.current) {
          failedStartSourceKeyRef.current = sourceKey;
          setStartError(
            getUserFacingErrorText(
              error,
              buildStaffErrorFallback("open the card form", "Refresh the page first."),
            ),
          );
        }
        return null;
      } finally {
        if (mountedRef.current) {
          setStarting(false);
        }
      }
    },
    [
      amountDueInCents,
      canStart,
      chamberId,
      eventId,
      invoiceId,
      purpose,
      refreshAfterRejectedStart,
      session,
      sourceId,
      sourceKey,
      startRegistrationCheckout,
      startSponsorshipCheckout,
    ],
  );

  const startSingleFlight = useCallback(
    async (
      mode: SecureCardCheckoutMode,
      fallbackForCheckoutSessionId?: string,
    ) =>
      runEventSalesSingleFlight({
        inFlightGuard: startInFlightRef,
        execute: () => executeStart(mode, fallbackForCheckoutSessionId),
      }),
    [executeStart],
  );

  useEffect(() => {
    if (
      !canStart ||
      session ||
      starting ||
      startError ||
      failedStartSourceKeyRef.current === sourceKey
    ) {
      return;
    }

    void startSingleFlight("CUSTOM");
  }, [
    canStart,
    session,
    sourceKey,
    startError,
    startRetryNonce,
    startSingleFlight,
    starting,
  ]);

  const handleRetryStart = () => {
    if (!canStart || session || starting) {
      return;
    }

    failedStartSourceKeyRef.current = "";
    setStartError(null);
    setStartRetryNonce((value) => value + 1);
  };

  if (!session) {
    return (
      <section
        aria-busy={starting}
        aria-live="polite"
        className="min-w-0 rounded-lg border border-gray-200 bg-gray-50 p-5"
        data-testid="event-secure-card-checkout-panel"
      >
        <h4 className="text-base font-bold text-black">Card Information</h4>
        {starting ? (
          <div className="mt-4 flex min-h-20 items-center justify-center gap-3 text-sm font-bold text-gray-700">
            <CircularProgress size={20} />
            Loading secure card form...
          </div>
        ) : startError ? (
          <div className="mt-4 space-y-3">
            <Alert severity="error">{startError}</Alert>
            <Button
              disabled={starting}
              onClick={handleRetryStart}
              sx={{ textTransform: "none" }}
              variant="contained"
            >
              Retry secure card form
            </Button>
          </div>
        ) : !canStart ? (
          <Alert className="mt-4" severity="info">
            {getEventSecureCardUnavailableMessage({
              fallback:
                "Card payment is not available for this sale right now. Refresh the page and try again, or select another payment method.",
              paymentStatus,
              purpose,
              sourceStatus,
            })}
          </Alert>
        ) : null}
        {beforeSubmit ? <div className="mt-4">{beforeSubmit}</div> : null}
      </section>
    );
  }

  return (
    <SecureCardCheckoutHandoff
      amountInCents={session.amountInCents}
      beforeSubmit={beforeSubmit}
      checkoutMode={session.checkoutMode}
      checkoutSessionId={session.checkoutSessionId}
      checkoutUrl={session.checkoutUrl}
      clientSecret={session.clientSecret}
      connectedAccountId={session.connectedAccountId}
      expiresAtUtcMilli={session.expiresAtUtcMilli}
      onBeforeConfirm={saveCardNoteBeforeConfirm}
      onPaymentInProgressChange={onPaymentInProgressChange}
      onPaymentSubmitted={async () => {
        await refreshAuthoritativeStatus();
      }}
      onPaymentProcessed={notifyPaymentProcessed}
      onRefreshStatus={async () => {
        await refreshAuthoritativeStatus();
      }}
      onRequestHostedFallback={async (
        customCheckoutSessionId,
      ): Promise<SecureCardCheckoutHandoffData | null> =>
        (await startSingleFlight("HOSTED_FALLBACK", customCheckoutSessionId)) ||
        null
      }
      publishableKey={session.publishableKey}
      refreshingStatus={false}
      requestingHostedFallback={starting}
      returnUrl={returnUrl}
      status={status}
      statusRefreshError={startError}
    />
  );
};

export default EventSecureCardCheckoutPanel;
